import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Page, Document as PdfDocument, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { customFieldService } from '../../services/custom-field.service'
import { documentTypeService } from '../../services/document-type.service'
import { documentService } from '../../services/document.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { taskService } from '../../services/task.service'
import { toastService } from '../../services/toast.service'
import { CustomField, Document, DocumentType } from '../../types'
import IfPermissions from '../common/IfPermissions'
import { InputSelect } from '../common/InputSelect'
import './TemplateEditorModal.scss'

if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
}

interface TemplateRule {
  id: number
  custom_field_id: number
  custom_field_name: string
  page_index: number
  anchor_text: string
  value_preview?: string
  value_bbox?: { x0: number; y0: number; x1: number; y1: number }
  mapped_bbox?: { x0: number; y0: number; x1: number; y1: number }
  preview_page_index?: number
}

interface AttributeValue {
  custom_field_id: number
  value: string
  rule_id?: number
}

interface TemplateEditorModalProps {
  documentType: DocumentType
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

export const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({ documentType, show, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const tx = (key: string, fallback: string) => {
    const value = t(key)
    return !value || value === key ? fallback : value
  }
  const selectPortalTarget = typeof document !== 'undefined' ? document.body : null
  const [sampleId, setSampleId] = useState<number | null>(null)
  const [pageCount, setPageCount] = useState(0)
  const [pageIndex, setPageIndex] = useState(0)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(true)
  const [selectedFileName, setSelectedFileName] = useState('')
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined)
  const [, setPageRenderVersion] = useState(0)
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [rules, setRules] = useState<TemplateRule[]>([])
  const [attributeValues, setAttributeValues] = useState<Record<number, AttributeValue>>({})
  const [templateActive, setTemplateActive] = useState(documentType.template_active ?? false)
  const [templateDocumentId, setTemplateDocumentId] = useState<number | null>(null)
  const [templateDocumentTitle, setTemplateDocumentTitle] = useState<string>('')
  const [availableDocuments, setAvailableDocuments] = useState<Document[]>([])
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null)
  const [linkingDocument, setLinkingDocument] = useState(false)
  const [activating, setActivating] = useState(false)
  const [selection, setSelection] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null)
  const [showMapModal, setShowMapModal] = useState(false)
  const [pendingBbox, setPendingBbox] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null)
  const [mappingFieldId, setMappingFieldId] = useState<number | null>(null)
  const [creatingRule, setCreatingRule] = useState(false)
  const [mapError, setMapError] = useState<string | null>(null)
  const [reprocessing, setReprocessing] = useState(false)
  const [filterMissing, setFilterMissing] = useState(false)
  const [hoveredRuleId, setHoveredRuleId] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragStart = useRef<{ x: number; y: number } | null>(null)

  const availableFields = customFields.filter(
    (f) => f.data_type !== 'separator' && documentType.custom_fields?.includes(f.id),
  )
  const filteredFields = filterMissing ? availableFields.filter((f) => !attributeValues[f.id]?.value) : availableFields
  const documentLabel = tx('document', 'Dokument')
  const availableDocumentOptions = useMemo(
    () =>
      availableDocuments.map((doc) => ({
        ...doc,
        display_name: `#${doc.id} ${doc.title || doc.original_file_name || documentLabel}`,
      })),
    [availableDocuments, documentLabel]
  )

  const loadSampleFile = useCallback(async () => {
    if (!sampleId) return
    setLoading(true)
    try {
      const blob = await documentTypeService.fetchTemplateSampleFile(documentType.id, sampleId)
      setBlobUrl(URL.createObjectURL(blob))
    } catch {
      toastService.showError(t('error_loading_data') || 'Greška pri učitavanju')
    } finally {
      setLoading(false)
    }
  }, [documentType.id, sampleId, t])

  const loadRules = useCallback(
    async (overrideSampleId?: number) => {
      const sid = overrideSampleId ?? sampleId ?? undefined
      try {
        const { rules: r } = await documentTypeService.getTemplateRules(documentType.id, sid)
        setRules(r)
        setAttributeValues((prev) => {
          const next = { ...prev }
          r.forEach((rule) => {
            const displayValue = rule.value_preview ?? rule.anchor_text
            next[rule.custom_field_id] = {
              custom_field_id: rule.custom_field_id,
              value: displayValue,
              rule_id: rule.id,
            }
          })
          return next
        })
      } catch {
        setRules([])
      }
    },
    [documentType.id, sampleId]
  )

  useEffect(() => {
    if (show && documentType) {
      setTemplateActive(documentType.template_active ?? false)
      setSampleId(null)
      setBlobUrl(null)
      setRules([])
      setAttributeValues({})
      setSelection(null)
      setPendingBbox(null)
      setShowMapModal(false)
      setIsFullscreen(true)
      setTemplateDocumentId(null)
      setTemplateDocumentTitle('')
      setSelectedDocumentId(null)
      setHoveredRuleId(null)
    }
  }, [show, documentType])

  useEffect(() => {
    if (!show) return
    const loadReference = async () => {
      try {
        const ref = await documentTypeService.getTemplateReferenceDocument(documentType.id)
        if (ref.template_document_id) {
          setTemplateDocumentId(ref.template_document_id)
          setTemplateDocumentTitle(ref.template_document_title || '')
          setSelectedDocumentId(ref.template_document_id)
        }
        const persistedSampleId = ref.sample_id || ref.template_sample_id
        if (persistedSampleId) {
          setSampleId(persistedSampleId)
        }
      } catch {
        setTemplateDocumentId(null)
        setTemplateDocumentTitle('')
      }
    }
    loadReference()
  }, [show, documentType.id])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await customFieldService.listAll()
        setCustomFields(res.results || [])
      } catch {
        setCustomFields([])
      }
    }
    if (show) load()
  }, [show])

  useEffect(() => {
    if (!show) return
    const loadDocuments = async () => {
      try {
        const res = await documentService.getDocuments({
          page: 1,
          page_size: 200,
          ordering: '-created',
        })
        setAvailableDocuments(res.results || [])
      } catch {
        setAvailableDocuments([])
      }
    }
    loadDocuments()
  }, [show, documentType.id])

  useEffect(() => {
    if (show && sampleId) {
      loadSampleFile()
      documentTypeService.getTemplateSampleInfo(documentType.id, sampleId).then((info) => {
        setPageCount(info.page_count)
      })
      loadRules()
    }
  }, [show, sampleId, documentType.id, loadSampleFile, loadRules])

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [blobUrl])

  useEffect(() => {
    if (!show) return
    const updatePageWidth = () => {
      if (!containerRef.current) return
      const width = Math.floor(containerRef.current.clientWidth - 24)
      setPageWidth(width > 0 ? width : undefined)
    }

    updatePageWidth()
    window.addEventListener('resize', updatePageWidth)

    let observer: ResizeObserver | null = null
    if (containerRef.current) {
      observer = new ResizeObserver(() => updatePageWidth())
      observer.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updatePageWidth)
      observer?.disconnect()
    }
  }, [show, isFullscreen, blobUrl])

  useEffect(() => {
    if (!show || !blobUrl) return
    const timer = window.setTimeout(() => {
      setPageRenderVersion((prev) => prev + 1)
    }, 60)
    return () => window.clearTimeout(timer)
  }, [show, blobUrl, isFullscreen, pageWidth, pageIndex])

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setSelectedFileName(file.name)
    setUploading(true)
    try {
      const result = await documentTypeService.uploadTemplateSample(documentType.id, file)
      if (result.error && !result.sample_id) {
        toastService.showError(result.error)
        return
      }
      if (result.error) {
        toastService.showError(result.error)
      }
      setSampleId(result.sample_id)
      setPageCount(result.page_count ?? 0)
      setPageIndex(0)
      loadRules(result.sample_id)
      if (result.dms_upload_task_id) {
        const taskId = result.dms_upload_task_id
        for (let i = 0; i < 20; i += 1) {
          const tasks = await taskService.getTasks({
            task_name: 'consume_file',
            acknowledged: false,
            ordering: '-date_created',
            page_size: 200,
          })
          const task = tasks.find((t) => t.task_id === taskId)
          if (task?.related_document) {
            const linked = await documentTypeService.setTemplateReferenceDocument(documentType.id, Number(task.related_document))
            setTemplateDocumentId(linked.template_document_id)
            setTemplateDocumentTitle(linked.template_document_title || '')
            setSampleId(linked.sample_id)
            setPageCount(linked.page_count ?? 0)
            await loadRules(linked.sample_id)
            break
          }
          if (task?.status === 'FAILURE') break
          await new Promise((resolve) => setTimeout(resolve, 1500))
        }
      }
    } catch {
      toastService.showError(t('error_loading_data') || 'Greška pri učitavanju')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const getContentCoords = (clientX: number, clientY: number) => {
    if (!containerRef.current) return { x: 0, y: 0 }
    const el = containerRef.current
    const rect = el.getBoundingClientRect()
    return {
      x: clientX - rect.left + el.scrollLeft,
      y: clientY - rect.top + el.scrollTop,
    }
  }

  const getPageMetrics = () => {
    if (!containerRef.current) return null
    const container = containerRef.current
    const canvasEl = container.querySelector('.react-pdf__Page canvas') as HTMLCanvasElement | null
    if (!canvasEl) return null
    const containerRect = container.getBoundingClientRect()
    const pageRect = canvasEl.getBoundingClientRect()
    return {
      left: pageRect.left - containerRect.left + container.scrollLeft,
      top: pageRect.top - containerRect.top + container.scrollTop,
      width: canvasEl.clientWidth,
      height: canvasEl.clientHeight,
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current || !blobUrl) return
    const page = getPageMetrics()
    if (!page) return
    const { x, y } = getContentCoords(e.clientX, e.clientY)
    const insidePage = x >= page.left && x <= page.left + page.width && y >= page.top && y <= page.top + page.height
    if (!insidePage) return
    dragStart.current = { x, y }
    setSelection({ x0: x, y0: y, x1: x, y1: y })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragStart.current || !containerRef.current) return
    const page = getPageMetrics()
    if (!page) return
    const { x, y } = getContentCoords(e.clientX, e.clientY)
    const clampedX = Math.max(page.left, Math.min(page.left + page.width, x))
    const clampedY = Math.max(page.top, Math.min(page.top + page.height, y))
    setSelection({
      x0: Math.min(dragStart.current.x, clampedX),
      y0: Math.min(dragStart.current.y, clampedY),
      x1: Math.max(dragStart.current.x, clampedX),
      y1: Math.max(dragStart.current.y, clampedY),
    })
  }

  const handleMouseUp = () => {
    if (!dragStart.current || !containerRef.current || !selection) return
    const page = getPageMetrics()
    if (!page || page.width <= 0 || page.height <= 0) {
      setSelection(null)
      dragStart.current = null
      return
    }
    if (selection.x1 - selection.x0 < 5 || selection.y1 - selection.y0 < 5) {
      setSelection(null)
      dragStart.current = null
      return
    }
    const x0 = Math.min(selection.x0, selection.x1)
    const y0 = Math.min(selection.y0, selection.y1)
    const x1 = Math.max(selection.x0, selection.x1)
    const y1 = Math.max(selection.y0, selection.y1)
    const bbox = {
      x0: Math.max(0, Math.min(1, (x0 - page.left) / page.width)),
      y0: Math.max(0, Math.min(1, (y0 - page.top) / page.height)),
      x1: Math.max(0, Math.min(1, (x1 - page.left) / page.width)),
      y1: Math.max(0, Math.min(1, (y1 - page.top) / page.height)),
    }
    setPendingBbox(bbox)
    setShowMapModal(true)
    setSelection(null)
    dragStart.current = null
  }

  const handleMapSubmit = async () => {
    if (!mappingFieldId || !pendingBbox || !sampleId) return
    setCreatingRule(true)
    setMapError(null)
    try {
      const result = await documentTypeService.createTemplateRule(documentType.id, {
        sample_id: sampleId,
        custom_field_id: mappingFieldId,
        page_index: pageIndex,
        value_bbox: pendingBbox,
      })
      const mappedFieldId = mappingFieldId
      const mappedValue = result.value_preview
      const mappedRuleId = result.rule_id
      setAttributeValues((prev) => ({
        ...prev,
        [mappedFieldId]: {
          custom_field_id: mappedFieldId,
          value: mappedValue,
          rule_id: mappedRuleId,
        },
      }))
      await loadRules()
      setAttributeValues((prev) => ({
        ...prev,
        [mappedFieldId]: {
          ...prev[mappedFieldId],
          value: mappedValue,
          rule_id: mappedRuleId,
        },
      }))
      setShowMapModal(false)
      setPendingBbox(null)
      setMappingFieldId(null)
      setMapError(null)
      toastService.showSuccess(t('template_mapped') || 'Mapirano')
      if (result.ocr_fallback_used) {
        toastService.showInfo('Mapiranje je uspelo uz OCR fallback za izabrani region.')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setMapError(msg || t('error_saving') || 'Greška')
      toastService.showError(msg || t('error_saving') || 'Greška')
    } finally {
      setCreatingRule(false)
    }
  }

  const handleUseExistingDocument = async (documentId: number) => {
    if (!documentId) return
    if (templateDocumentId === documentId) return
    setLinkingDocument(true)
    try {
      const res = await documentTypeService.setTemplateReferenceDocument(documentType.id, documentId)
      setTemplateDocumentId(res.template_document_id)
      setTemplateDocumentTitle(res.template_document_title || '')
      setSelectedDocumentId(res.template_document_id)
      setSampleId(res.sample_id)
      setPageCount(res.page_count ?? 0)
      setPageIndex(0)
      await loadRules(res.sample_id)
      toastService.showSuccess(t('template_mapped') || 'Dokument postavljen za mapiranje')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toastService.showError(msg || t('error_saving') || 'Greška')
    } finally {
      setLinkingDocument(false)
    }
  }

  const handleReprocessOcr = async () => {
    if (!sampleId) return
    setReprocessing(true)
    try {
      await documentTypeService.reprocessTemplateSample(documentType.id, sampleId)
      toastService.showSuccess(t('template_ocr_reprocessed') || 'OCR ponovo pokrenut')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toastService.showError(msg || t('error_saving') || 'Greška')
    } finally {
      setReprocessing(false)
    }
  }

  const handleDeleteRule = async (fieldId: number) => {
    const rule = rules.find((r) => r.custom_field_id === fieldId)
    if (!rule) return
    try {
      await documentTypeService.deleteTemplateRule(documentType.id, rule.id)
      setAttributeValues((prev) => {
        const next = { ...prev }
        delete next[fieldId]
        return next
      })
      await loadRules()
    } catch {
      toastService.showError(t('error_saving') || 'Greška')
    }
  }

  const handleActivate = async () => {
    setActivating(true)
    try {
      await documentTypeService.activateTemplate(documentType.id, !templateActive)
      setTemplateActive(!templateActive)
      onSuccess()
      toastService.showSuccess(templateActive ? t('template_deactivated') || 'Šablon deaktiviran' : t('template_activated') || 'Šablon aktiviran')
    } catch {
      toastService.showError(t('error_saving') || 'Greška')
    } finally {
      setActivating(false)
    }
  }

  const modalBodyStyle = isFullscreen ? { height: 'calc(100vh - 118px)' } : undefined
  const contentRowStyle = isFullscreen ? { minHeight: '100%', height: '100%' } : { minHeight: 500 }
  const previewContainerStyle = isFullscreen
    ? { height: '100%', maxHeight: '100%', cursor: blobUrl ? 'crosshair' : 'default' }
    : { minHeight: 400, maxHeight: 500, cursor: blobUrl ? 'crosshair' : 'default' }

  return (
    <Modal
      show={show}
      onHide={onClose}
      size={isFullscreen ? undefined : 'xl'}
      fullscreen={isFullscreen ? true : undefined}
      className="template-editor-modal"
    >
      <Form.Control
        id="template-upload-input"
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        onChange={handleFileUpload}
        disabled={uploading}
        style={{ display: 'none' }}
      />
      <Modal.Header closeButton>
        <Modal.Title className="me-3">
          {tx('template_mapping', 'Mapiranje šablona')} – {documentType.name}
        </Modal.Title>
        <div className="d-flex align-items-center gap-2 flex-wrap ms-4 flex-grow-1">
          <Button
            variant="outline-secondary"
            size="sm"
            style={{ minHeight: 38 }}
            onClick={() => document.getElementById('template-upload-input')?.click()}
            disabled={uploading}
          >
            <i className="bi bi-cloud-upload me-1" />
            {tx('document', 'Dokument')}
          </Button>
          <div className="small text-muted" style={{ minWidth: 140 }}>
            {selectedFileName || tx('no_file_selected', 'Nije izabran fajl')}
          </div>
          {uploading && <Spinner animation="border" size="sm" />}
          <div className="template-editor-modal__document-select" style={{ width: 320, maxWidth: '100%' }}>
            <InputSelect
              value={selectedDocumentId}
              onChange={async (value) => {
                const next = typeof value === 'number' ? value : null
                setSelectedDocumentId(next)
                if (next) {
                  await handleUseExistingDocument(next)
                }
              }}
              items={availableDocumentOptions}
              bindLabel="display_name"
              placeholder={tx('select_document', 'Odaberi dokument iz DMS-a')}
              disabled={linkingDocument}
              menuPortalTarget={selectPortalTarget}
            />
          </div>
          {linkingDocument && <Spinner animation="border" size="sm" />}
          {sampleId && (
            <>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleReprocessOcr}
                disabled={reprocessing}
                title={tx('template_reprocess_ocr', 'Ponovo pokreni OCR')}
                style={{ minHeight: 38 }}
              >
                {reprocessing ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-clockwise me-1" />}
                OCR
              </Button>
              <div className="btn-group">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pageIndex <= 0}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  style={{ minHeight: 38 }}
                >
                  <i className="bi bi-chevron-left" />
                </Button>
                <Button variant="outline-secondary" size="sm" disabled style={{ minHeight: 38 }}>
                  {pageIndex + 1} / {pageCount}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pageIndex >= pageCount - 1}
                  onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
                  style={{ minHeight: 38 }}
                >
                  <i className="bi bi-chevron-right" />
                </Button>
              </div>
            </>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            className="ms-auto"
            onClick={() => setIsFullscreen((prev) => !prev)}
            style={{ minHeight: 38 }}
            title={isFullscreen ? tx('exit_fullscreen', 'Izadji iz celog ekrana') : tx('fullscreen', 'Ceo ekran')}
          >
            <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className="p-0" style={modalBodyStyle}>
        <Row className="g-0" style={contentRowStyle}>
          <Col md={8} className="border-end p-3 d-flex flex-column h-100">
            <div
              ref={containerRef}
              className="position-relative border rounded bg-light overflow-auto"
              style={previewContainerStyle}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {loading && (
                <div className="position-absolute top-50 start-50 translate-middle">
                  <Spinner animation="border" />
                </div>
              )}
              {blobUrl && !loading && (
                <PdfDocument file={blobUrl}>
                  <Page
                    pageNumber={pageIndex + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => setPageRenderVersion((prev) => prev + 1)}
                  />
                </PdfDocument>
              )}
              {containerRef.current &&
                (() => {
                  const page = getPageMetrics()
                  if (!page) return null
                  const bboxPadding = 4
                  return rules
                    .filter((r) => (r.mapped_bbox || r.value_bbox) && (r.preview_page_index ?? r.page_index) === pageIndex)
                    .map((r) => {
                      const bbox = r.mapped_bbox || r.value_bbox!
                      const left = page.left + bbox.x0 * page.width - bboxPadding
                      const top = page.top + bbox.y0 * page.height - bboxPadding
                      const width = (bbox.x1 - bbox.x0) * page.width + bboxPadding * 2
                      const height = (bbox.y1 - bbox.y0) * page.height + bboxPadding * 2
                      return (
                        <div
                          key={`overlay-${r.id}`}
                          className="position-absolute rounded"
                          style={{
                            left,
                            top,
                            width,
                            height,
                            pointerEvents: 'auto',
                            border: '2px solid var(--bs-primary)',
                            backgroundColor: 'rgba(var(--bs-primary-rgb), 0.25)',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                          }}
                          onMouseEnter={() => setHoveredRuleId(r.id)}
                          onMouseLeave={() => setHoveredRuleId((prev) => (prev === r.id ? null : prev))}
                        >
                          {hoveredRuleId === r.id && (
                            <span className="badge bg-primary text-primary-contrast" style={{ position: 'absolute', top: -20, left: 0 }}>
                              {r.custom_field_name}
                            </span>
                          )}
                        </div>
                      )
                    })
                })()}
              {selection && containerRef.current && (
                <div
                  className="position-absolute rounded"
                  style={{
                    left: selection.x0,
                    top: selection.y0,
                    width: selection.x1 - selection.x0,
                    height: selection.y1 - selection.y0,
                    pointerEvents: 'none',
                    border: '2px solid var(--bs-primary)',
                    backgroundColor: 'rgba(var(--bs-primary-rgb), 0.25)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                  }}
                />
              )}
              {!sampleId && !uploading && (
                <div className="position-absolute top-50 start-50 translate-middle text-muted text-center">
                  <i className="bi bi-file-earmark-pdf display-4" />
                  <p className="mt-2 mb-0">{t('template_upload_sample') || 'Učitaj primer dokumenta (PDF)'}</p>
                </div>
              )}
            </div>
          </Col>
          <Col md={4} className="p-3 d-flex flex-column h-100">
            {templateDocumentId && (
              <div className="small text-muted mb-2">
                {t('document') || 'Dokument'}: #{templateDocumentId} {templateDocumentTitle}
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">{t('attribute_mappings') || 'Mapiranja atributa'}</h6>
              <Form.Check
                type="switch"
                label={t('template_active') || 'Aktiviran'}
                checked={templateActive}
                onChange={handleActivate}
                disabled={activating || rules.length === 0}
              />
            </div>
            <Form.Check
              type="checkbox"
              label={t('template_show_missing') || 'Prikaži samo polja koja nedostaju'}
              checked={filterMissing}
              onChange={(e) => setFilterMissing(e.target.checked)}
              className="mb-2"
            />
            <div className="border rounded p-2" style={{ maxHeight: isFullscreen ? '100%' : 420, overflow: 'auto' }}>
              {filteredFields.length === 0 ? (
                <p className="text-muted mb-0 small">
                  {availableFields.length === 0
                    ? t('no_attributes_for_type') || 'Nema atributa dodeljenih ovom tipu dokumenta.'
                    : t('template_all_mapped') || 'Sva polja su mapirana.'}
                </p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {filteredFields.map((field) => {
                    const val = attributeValues[field.id]
                    return (
                      <div key={field.id} className="border-bottom pb-2 d-flex justify-content-between align-items-start gap-2">
                        <div className="flex-grow-1 min-w-0">
                          <div className="small fw-semibold">{field.name}</div>
                          <div className="small text-muted">
                            {val?.value ? <span className="text-success">{val.value}</span> : <span className="text-muted">—</span>}
                          </div>
                        </div>
                        <div className="d-flex gap-1">
                          {val?.rule_id && (
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteRule(field.id)} title={t('delete') || 'Obriši'}>
                              <i className="bi bi-trash" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <small className="text-muted me-auto">
          {t('template_mapping_info') ||
            'Selektuj oblast na dokumentu, izaberi atribut i klikni Mapiraj. Sistem automatski pronalazi label (anchor) i čuva pravilo.'}
        </small>
        <Button variant="secondary" onClick={onClose}>
          {t('close') || 'Zatvori'}
        </Button>
      </Modal.Footer>

      <Modal show={showMapModal} onHide={() => !creatingRule && (setShowMapModal(false), setMapError(null))} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('template_map_attribute') || 'Mapiraj na atribut'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mapError && (
            <div className="alert alert-danger py-2 mb-2" role="alert">
              {mapError}
            </div>
          )}
          <Form.Group className="mb-2">
            <Form.Label>{t('attribute') || 'Atribut'}</Form.Label>
            <Form.Select value={mappingFieldId ?? ''} onChange={(e) => setMappingFieldId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">—</option>
              {availableFields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMapModal(false)} disabled={creatingRule}>
            {t('cancel')}
          </Button>
          <IfPermissions action={PermissionAction.Change} type={PermissionType.DocumentType}>
            <Button variant="primary" onClick={handleMapSubmit} disabled={!mappingFieldId || creatingRule}>
              {creatingRule ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('saving') || 'Čuvanje...'}
                </>
              ) : (
                t('template_map_button') || 'Mapiraj'
              )}
            </Button>
          </IfPermissions>
        </Modal.Footer>
      </Modal>
    </Modal>
  )
}
